/**
 * Branded share cards for viral hooks (county lookup, rivalry, weekly swing, etc.).
 * OG-friendly 1200×630 PNG — no map canvas required.
 */

export interface ShareCardOptions {
    headline: string;
    subhead?: string;
    monoBlock?: string;
    kicker?: string;
    theme?: 'default' | 'all-time';
    accentColor?: string;
    badge?: string;
    teamLogoUrl?: string | null;
    footer?: string;
    width?: number;
    height?: number;
}

const DEFAULT_W = 1200;
const DEFAULT_H = 630;
const BRAND_ORANGE = '#f97316';
const LEGENDS_GOLD = '#fbbf24';

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = word;
        } else {
            line = test;
        }
    }
    if (line) lines.push(line);
    return lines.slice(0, maxLines);
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = src;
    });
}

function drawShield(ctx: CanvasRenderingContext2D, x: number, y: number, s: number) {
    ctx.save();
    ctx.fillStyle = BRAND_ORANGE;
    ctx.beginPath();
    ctx.moveTo(x + s * 0.5, y);
    ctx.lineTo(x + s, y + s * 0.18);
    ctx.lineTo(x + s, y + s * 0.55);
    ctx.quadraticCurveTo(x + s, y + s * 0.86, x + s * 0.5, y + s);
    ctx.quadraticCurveTo(x, y + s * 0.86, x, y + s * 0.55);
    ctx.lineTo(x, y + s * 0.18);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
}

export async function renderShareCard(options: ShareCardOptions): Promise<Blob> {
    const w = options.width ?? DEFAULT_W;
    const h = options.height ?? DEFAULT_H;
    const pad = Math.round(w * 0.06);
    const isAllTime = options.theme === 'all-time';
    const accent = options.accentColor || (isAllTime ? LEGENDS_GOLD : BRAND_ORANGE);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');

    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, isAllTime ? '#0a0f1a' : '#0b1120');
    bg.addColorStop(1, isAllTime ? '#1a1208' : '#111827');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    if (isAllTime) {
        ctx.fillStyle = 'rgba(251, 191, 36, 0.06)';
        ctx.beginPath();
        ctx.arc(w * 0.12, h * 0.78, w * 0.28, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(w * 0.88, h * 0.18, w * 0.22, 0, Math.PI * 2);
        ctx.fill();
    }

    ctx.fillStyle = accent;
    ctx.globalAlpha = isAllTime ? 0.18 : 0.12;
    ctx.beginPath();
    ctx.arc(w * 0.85, h * 0.2, w * 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    if (isAllTime) {
        const barGrad = ctx.createLinearGradient(0, 0, w, 0);
        barGrad.addColorStop(0, LEGENDS_GOLD);
        barGrad.addColorStop(0.45, accent);
        barGrad.addColorStop(1, LEGENDS_GOLD);
        ctx.fillStyle = barGrad;
        ctx.fillRect(0, 0, w, 10);
    } else {
        ctx.fillStyle = accent;
        ctx.fillRect(0, 0, w, 8);
    }

    if (isAllTime) {
        ctx.strokeStyle = 'rgba(251, 191, 36, 0.22)';
        ctx.lineWidth = 3;
        roundRect(ctx, pad * 0.5, pad * 0.5, w - pad, h - pad, 18);
        ctx.stroke();
    }

    drawShield(ctx, pad, pad, Math.round(h * 0.09));

    ctx.fillStyle = '#94a3b8';
    ctx.font = `700 ${Math.round(h * 0.045)}px system-ui, sans-serif`;
    ctx.fillText('Empire Maps', pad + h * 0.11, pad + h * 0.065);

    if (options.badge) {
        ctx.font = `700 ${Math.round(h * 0.035)}px system-ui, sans-serif`;
        const badgeW = ctx.measureText(options.badge).width + 24;
        roundRect(ctx, w - pad - badgeW, pad, badgeW, Math.round(h * 0.06), 8);
        ctx.fillStyle = isAllTime ? 'rgba(251,191,36,0.12)' : 'rgba(249,115,22,0.15)';
        ctx.fill();
        ctx.strokeStyle = isAllTime ? LEGENDS_GOLD : accent;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = isAllTime ? LEGENDS_GOLD : accent;
        ctx.fillText(options.badge, w - pad - badgeW + 12, pad + h * 0.042);
    }

    let textX = pad;
    let y = pad + h * 0.22;

    if (options.kicker) {
        ctx.fillStyle = LEGENDS_GOLD;
        ctx.font = `700 ${Math.round(h * 0.034)}px system-ui, sans-serif`;
        ctx.fillText(options.kicker, textX, pad + h * 0.15);
        y = pad + h * 0.26;
    }
    if (options.teamLogoUrl) {
        try {
            const logo = await loadImage(options.teamLogoUrl);
            const logoSize = Math.round(h * 0.22);
            const lx = w - pad - logoSize;
            const ly = h - pad - logoSize;
            roundRect(ctx, lx - 4, ly - 4, logoSize + 8, logoSize + 8, 12);
            ctx.fillStyle = 'rgba(255,255,255,0.06)';
            ctx.fill();
            ctx.drawImage(logo, lx, ly, logoSize, logoSize);
        } catch {
            /* optional logo */
        }
    }

    ctx.fillStyle = '#f8fafc';
    ctx.font = `800 ${Math.round(h * 0.11)}px system-ui, sans-serif`;
    const headlineLines = wrapText(ctx, options.headline, w - pad * 2 - (options.teamLogoUrl ? h * 0.28 : 0), 3);
    headlineLines.forEach((line) => {
        ctx.fillText(line, textX, y);
        y += Math.round(h * 0.12);
    });

    if (options.subhead) {
        ctx.fillStyle = '#cbd5e1';
        ctx.font = `600 ${Math.round(h * 0.055)}px system-ui, sans-serif`;
        const subLines = wrapText(ctx, options.subhead, w - pad * 2, 2);
        subLines.forEach((line) => {
            ctx.fillText(line, textX, y);
            y += Math.round(h * 0.07);
        });
    }

    if (options.monoBlock) {
        y += Math.round(h * 0.02);
        ctx.fillStyle = isAllTime ? '#fcd34d' : '#94a3b8';
        ctx.font = `500 ${Math.round(h * 0.032)}px ui-monospace, "Cascadia Code", Consolas, monospace`;
        roundRect(ctx, textX - 8, y - Math.round(h * 0.035), w - pad * 2 - (options.teamLogoUrl ? h * 0.28 : 0) + 16, Math.round(h * 0.19), 8);
        ctx.fillStyle = isAllTime ? 'rgba(251,191,36,0.08)' : 'transparent';
        ctx.fill();
        ctx.fillStyle = isAllTime ? '#fde68a' : '#94a3b8';
        const monoLines = options.monoBlock.split('\n').slice(0, 4);
        monoLines.forEach((line) => {
            ctx.fillText(line, textX, y);
            y += Math.round(h * 0.045);
        });
    }

    ctx.fillStyle = isAllTime ? '#a8a29e' : '#64748b';
    ctx.font = `700 ${Math.round(h * 0.038)}px system-ui, sans-serif`;
    const footer = options.footer || 'EmpireMaps.live';
    ctx.fillText(footer, pad, h - pad);

    return new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('Failed to encode share card'));
        }, 'image/png');
    });
}
